import React, { useState } from "react";
import { Loader2, ClipboardPaste, Mic } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CANALES = ["whatsapp", "sms", "email"];

export const IntakeManualPasteDialog = ({ open, onOpenChange, onCreated }) => {
  const { t } = useT();
  const [modo, setModo] = useState("texto");
  const [canal, setCanal] = useState("whatsapp");
  const [remitenteContacto, setRemitenteContacto] = useState("");
  const [remitenteNombre, setRemitenteNombre] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [file, setFile] = useState(null);
  // Fix N (2026-09-28): fotos que vinieron en la conversación.
  const [fotos, setFotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setModo("texto"); setCanal("whatsapp"); setRemitenteContacto("");
    setRemitenteNombre(""); setCuerpo(""); setFile(null); setFotos([]);
  };

  const submit = async () => {
    if (modo === "texto" && cuerpo.trim().length < 3) {
      toast.error(t("intake.manual.body_required"));
      return;
    }
    if (modo === "audio" && !file) {
      toast.error(t("intake.manual.file_required"));
      return;
    }
    setSaving(true);
    try {
      let data;
      if (modo === "audio") {
        const form = new FormData();
        form.append("file", file);
        if (remitenteContacto) form.append("remitente_contacto", remitenteContacto);
        if (remitenteNombre) form.append("remitente_nombre", remitenteNombre);
        ({ data } = await api.post("/intake/voice/simulate", form));
      } else {
        ({ data } = await api.post("/intake/manual", {
          canal, remitente_contacto: remitenteContacto, remitente_nombre: remitenteNombre || undefined,
          cuerpo,
        }));
        if (fotos.length && data.mensaje?.id) {
          const form = new FormData();
          fotos.forEach((f) => form.append("files", f));
          try {
            await api.post(`/intake/messages/${data.mensaje.id}/attachments`, form);
          } catch (e) {
            toast.error(apiError(e, t("intake.manual.photos_error")));
          }
        }
      }
      const r = data.resultado;
      if (r?.estado === "no_accionable") toast.success(t("intake.manual.result_no_accionable"));
      else if (r?.estado === "agrupado") toast.success(t("intake.manual.result_grouped"));
      else toast.success(t("intake.manual.result_created", { n: r?.wo_ids?.length || 0 }));
      onCreated?.(data.mensaje?.id);
      onOpenChange(false);
      reset();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent data-testid="intake-manual-dialog" className="max-w-[560px] rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("intake.manual.title")}</DialogTitle>
          <DialogDescription className="text-[13px]">{t("intake.manual.subtitle")}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 rounded-[8px] bg-line p-1">
          <button data-testid="intake-manual-mode-text" onClick={() => setModo("texto")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-[6px] py-1.5 text-[12.5px] font-medium ${modo === "texto" ? "bg-white text-ink shadow-sm" : "text-ink-tertiary"}`}>
            <ClipboardPaste className="h-3.5 w-3.5" /> {t("intake.manual.mode_text")}
          </button>
          <button data-testid="intake-manual-mode-audio" onClick={() => setModo("audio")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-[6px] py-1.5 text-[12.5px] font-medium ${modo === "audio" ? "bg-white text-ink shadow-sm" : "text-ink-tertiary"}`}>
            <Mic className="h-3.5 w-3.5" /> {t("intake.manual.mode_audio")}
          </button>
        </div>

        {modo === "texto" && (
          <div>
            <Label className="text-[12.5px]">{t("intake.manual.channel")}</Label>
            <Select value={canal} onValueChange={setCanal}>
              <SelectTrigger data-testid="intake-manual-canal" className="mt-1 h-9 rounded-[6px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CANALES.map((c) => <SelectItem key={c} value={c}>{t(`intake.channel.${c}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-[12.5px]">{t("intake.manual.sender_contact")}</Label>
            <Input data-testid="intake-manual-remitente-contacto" value={remitenteContacto}
              onChange={(e) => setRemitenteContacto(e.target.value)}
              placeholder={t("intake.manual.sender_contact_ph")} className="mt-1 h-9 rounded-[6px] text-[13px]" />
          </div>
          <div>
            <Label className="text-[12.5px]">{t("intake.manual.sender_name")}</Label>
            <Input data-testid="intake-manual-remitente-nombre" value={remitenteNombre}
              onChange={(e) => setRemitenteNombre(e.target.value)}
              placeholder={t("intake.manual.sender_name_ph")} className="mt-1 h-9 rounded-[6px] text-[13px]" />
          </div>
        </div>

        {modo === "texto" ? (
          <div>
            <Label className="text-[12.5px]">{t("intake.manual.body")}</Label>
            <Textarea data-testid="intake-manual-body" value={cuerpo} rows={6}
              onChange={(e) => setCuerpo(e.target.value)} placeholder={t("intake.manual.body_ph")}
              className="mt-1 rounded-[6px] text-[13px]" />
            <Label className="mt-3 block text-[12.5px]">{t("intake.manual.photos")}</Label>
            <input type="file" multiple accept="image/*,.heic,.heif" data-testid="intake-manual-photos"
              onChange={(e) => setFotos(Array.from(e.target.files || []))}
              className="mt-1 block w-full text-[12.5px] text-ink-secondary" />
            {!!fotos.length && (
              <p className="mt-1 text-[11.5px] text-ink-tertiary">{t("intake.manual.photos_count", { n: fotos.length })}</p>
            )}
          </div>
        ) : (
          <div>
            <Label className="text-[12.5px]">{t("intake.manual.audio_file")}</Label>
            <input type="file" accept="audio/*" data-testid="intake-manual-audio-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-[12.5px] text-ink-secondary" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-[6px] text-[13px]">
            {t("app.cancel")}
          </Button>
          <Button data-testid="intake-manual-submit" disabled={saving} onClick={submit}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover">
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("intake.manual.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
